import axios from 'axios';
import Qs from 'qs';
import { defaultConfig } from '../config';
import { apiUrls } from './apiUrls';
import { store } from '../store/store';
import { showLoader, hideLoader } from '../store/slices/loaderSlice';
 import {
  getOfflineMenuCategories,
  getOfflineMenuProducts
} from '../utils/androidOfflineMenu';


const serializerConfig = {
  arrayFormat: 'brackets',
  encode: false,
};

const handleLogout = () => {

  // NEVER logout from the offline Android POS session
  // just because the API/network is unavailable.
  if (isOfflineAndroid()) {
    console.warn(
      '[OFFLINE_AUTH] Logout skipped because Android POS is offline'
    );
    return;
  }

  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('orderart_session_id');
  localStorage.removeItem('order_id');
  localStorage.removeItem('orderType');

  window.location.href = '/';
};

const refreshAccessToken = async () => {
  try {
    const API_ROOT = defaultConfig.baseAPIUrl;
    const refreshToken = localStorage.getItem("refreshToken");
    const userId = localStorage.getItem('userId');

    const response = await axios({
      method: 'post',
      url: API_ROOT + apiUrls.REFRESH_TOKEN_URL,
      data: { refresh_token: refreshToken, userId },
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (response.data?.access_token) {
      localStorage.setItem("accessToken", response.data.access_token);
      localStorage.setItem("idToken", response.data.IdToken);
      return response.data.access_token;
    }

    return false;

  } catch (error) {
    console.error("Failed to refresh access token:", error);
    throw error;
  }
};


const OFFLINE_CACHEABLE_ENDPOINTS = new Set([
  apiUrls.TABLE_DATA,
  apiUrls.OCCUPIED_TABLE_WITH_ORDERS
  
]);

const isOfflineCacheable = (path, method) => {
  return (
    method.toLowerCase() === 'get' &&
    OFFLINE_CACHEABLE_ENDPOINTS.has(path)
  );
};



const isAndroidPOS = () => {
  return (
    typeof window !== 'undefined' &&
    window.AndroidPOS &&
    typeof window.AndroidPOS.isAndroidPOS === 'function' &&
    window.AndroidPOS.isAndroidPOS() === true
  );
};

const isOfflineAndroid = () => {
  return (
    isAndroidPOS() &&
    typeof window.AndroidPOS.isOfflineMode === 'function' &&
    window.AndroidPOS.isOfflineMode() === true
  );
};

const getOfflineApiResponse = (endpoint) => {
  return new Promise((resolve, reject) => {
    console.log( '[OFFLINE_CACHE] REQUESTING FROM ANDROID', endpoint );

    if (!isAndroidPOS()) {
      console.error( '[OFFLINE_CACHE] Android bridge unavailable' );
      reject(new Error('Android POS bridge unavailable'));
      return;
    }

    
    const requestId =
      `offline_${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 10)}`;

    if (!window.__orderArtOfflineCallbacks) {
      window.__orderArtOfflineCallbacks = {};
    }

    window.__orderArtOfflineCallbacks[requestId] = {
      resolve,
      reject,
    };

    console.log( '[OFFLINE_CACHE] CALL AndroidPOS.getOfflineApiResponse', { requestId, endpoint } );

    window.AndroidPOS.getOfflineApiResponse(
      requestId,
      endpoint
    );

    setTimeout(() => {

      if (
        window.__orderArtOfflineCallbacks[requestId]
      ) {

        delete window.__orderArtOfflineCallbacks[
          requestId
        ];

        console.error( '[OFFLINE_CACHE] TIMEOUT', endpoint );
        reject(
          new Error(
            `Offline API timeout: ${endpoint}`
          )
        );
      }

    }, 10000);
  });
};

if (typeof window !== 'undefined') {

  window.__orderArtOfflineCallback = (
    requestId,
    responseJson
  ) => {

    const callback =
      window.__orderArtOfflineCallbacks?.[
        requestId
      ];

    if (!callback) {
      return;
    }

    delete window.__orderArtOfflineCallbacks[
      requestId
    ];

    if (!responseJson) {

      callback.reject(
        new Error(
          'No offline response available'
        )
      );

      return;
    }

    try {

      callback.resolve(
        JSON.parse(responseJson)
      );

    } catch (error) {

      callback.reject(error);
    }
  };
}


const OFFLINE_CART_ENDPOINTS = new Set([
  apiUrls.GET_ADDED_TO_CART,
  apiUrls.GET_CART_BY_ID,
  apiUrls.GET_CART_ITEMS_BY_TABLE
]);

const OFFLINE_MENU_ENDPOINTS = new Set([
  apiUrls.CATEGORY_LISTING,
  apiUrls.MENU_DISHES
]);

const isOfflineMenuEndpoint = (path) => {
  return OFFLINE_MENU_ENDPOINTS.has(path);
};


async function callAPI(
  path,
  params = {},
  method = 'get',
  data = null,
  options = {},
  headersObj = {},
  withLoader = true
) {

  const API_ROOT =
    defaultConfig.baseAPIUrl;

  const url =
    API_ROOT + path;

  const userId =
    localStorage.getItem('userId');

  const token =
    localStorage.getItem('accessToken');

  const headers = {
    Accept: 'application/json',
    'x-user-id': userId,
    ...(token && {
      Authorization: `Bearer ${token}`
    }),
    ...headersObj
  };

  params = {
    ...params,
    _cacheBuster: Date.now()
  };

  const sessionId =
    localStorage.getItem('sessionId');

  if (sessionId) {
    params.sessionId = sessionId;
  }

  const shouldShowLoader =
    withLoader;



const offlineAndroid =
    isOfflineAndroid();

const isGet =
    method.toLowerCase() === 'get';

  if (shouldShowLoader) {
    store.dispatch(showLoader());
  }

  try {

    /*
     * =====================================================
     * ONLINE REQUEST
     * =====================================================
     */

    const response =
      await axios({
        method,
        url,
        params,
        paramsSerializer: (p) =>
          Qs.stringify(
            p,
            serializerConfig
          ),
        data,
        headers,
        ...options,
      });

    /*
     * =====================================================
     * CACHE SUCCESSFUL RESPONSE ON ANDROID
     * =====================================================
     *
     * We cache only GET requests for now.
     */

  console.log(
  '[OFFLINE_CACHE] ONLINE SUCCESS',
  {
    path,
    method,
    cacheable: isOfflineCacheable(path, method),
    data: response.data
  }
);

if (
  isAndroidPOS() &&
  isOfflineCacheable(path, method)
) {

  console.log( '[OFFLINE_CACHE] SAVING', { endpoint: path } );
      try {

        window.AndroidPOS
          .saveOfflineApiResponse(
            path,
            JSON.stringify(
              response.data
            )
          );

      } catch (cacheError) {

        console.warn(
          'Failed to cache API response:',
          cacheError
        );
      }
    }

    if (
  isAndroidPOS() &&
  method.toLowerCase() === 'get'
) {

  try {

    if (
      path === apiUrls.CATEGORY_LISTING &&
      typeof window.AndroidPOS.saveOfflineMenuCategories === 'function'
    ) {

      window.AndroidPOS.saveOfflineMenuCategories(
        JSON.stringify(response.data)
      );

      console.log(
        '[MENU_CACHE] Categories saved to Room'
      );
    }


    if (
      path === apiUrls.MENU_DISHES &&
      typeof window.AndroidPOS.saveOfflineMenuProducts === 'function'
    ) {

      window.AndroidPOS.saveOfflineMenuProducts(
        JSON.stringify(response.data)
      );

      console.log(
        '[MENU_CACHE] Products saved to Room'
      );
    }

  } catch (error) {

    console.warn(
      '[MENU_CACHE] Failed saving menu to Room',
      error
    );
  }
}

if (
    offlineAndroid &&
    isGet &&
    isOfflineMenuEndpoint(path)
) {

    console.log(
        '[MENU_OFFLINE] Reading:',
        path
    );

    if (
        path === apiUrls.CATEGORY_LISTING
    ) {

        const data =
            await getOfflineMenuCategories();

        return {
            data,
            status: 200,
            statusText: 'OK - OFFLINE',
            headers: {},
            config: {},
            request: null,
            offline: true
        };
    }


    if (
        path === apiUrls.MENU_DISHES
    ) {

        const data =
            await getOfflineMenuProducts();

        return {
            data: {
                data
            },
            status: 200,
            statusText: 'OK - OFFLINE',
            headers: {},
            config: {},
            request: null,
            offline: true
        };
    }
}

    return response;

  } catch (error) {

    /*
     * =====================================================
     * OFFLINE FALLBACK
     * =====================================================
     *
     * Only GET requests should fall back to
     * cached/local data.
     */

    const isGet =
      method.toLowerCase() === 'get';

    const isAndroid =
      isAndroidPOS();

    /*
     * Axios network errors generally have no
     * response object.
     *
     * Examples:
     * - no internet
     * - DNS failure
     * - connection refused
     * - timeout
     */

const isNetworkError =
  !error.response ||
  error.code === 'ERR_NETWORK' ||
  error.code === 'ECONNABORTED' ||
  error.code === 'ETIMEDOUT';

  console.error( '[OFFLINE_CACHE] API FAILED', { path, method, status: error.response?.status, code: error.code, message: error.message, isGet, isAndroid, isNetworkError, cacheable: isOfflineCacheable(path, method) } );

    if (
      isAndroid &&
      isGet &&
      isNetworkError &&
      isOfflineCacheable(path, method)
    ) {

      console.warn(
        `API unavailable. Trying offline cache: ${path}`
      );

      try {

        const offlineData =
          await getOfflineApiResponse(
            path
          );

          console.log(
  '[OFFLINE] ROOM CACHE RESPONSE',
  {
    path,
    data: offlineData
  }
);

        if (offlineData) {

          /*
           * Return an Axios-like response
           * so existing dashboard code does
           * not need to change.
           */

          return {
            data: offlineData,
            status: 200,
            statusText: 'OK - OFFLINE',
            headers: {},
            config: {},
            request: null,
            offline: true
          };
        }

      } catch (offlineError) {

        console.error(
          `Offline fallback failed for ${path}:`,
          offlineError
        );
      }
    }

    /*
     * =====================================================
     * TOKEN REFRESH
     * =====================================================
     */

    if (
      error.response?.status === 401 &&
      !options._retry
    ) {

      options._retry = true;

      try {

        const newToken =
          await refreshAccessToken();

        if (newToken) {

          headers.Authorization =
            `Bearer ${newToken}`;

          const retryResponse =
            await axios({
              method,
              url,
              params,
              paramsSerializer: (p) =>
                Qs.stringify(
                  p,
                  serializerConfig
                ),
              data,
              headers,
              ...options,
            });

          /*
           * Cache successful retry response too.
           */

          if (
            isAndroidPOS() &&
            isGet
          ) {

            try {

              window.AndroidPOS
                .saveOfflineApiResponse(
                  path,
                  JSON.stringify(
                    retryResponse.data
                  )
                );

            } catch (cacheError) {

              console.warn(
                'Failed to cache retry response:',
                cacheError
              );
            }
          }

          return retryResponse;
        }

      } catch (refreshError) {

        console.error(
          "Refresh failed:",
          refreshError
        );

        handleLogout();
      }
    }

    throw error;

  } finally {

    if (shouldShowLoader) {
      store.dispatch(
        hideLoader()
      );
    }
  }
}





export { callAPI, refreshAccessToken };